import {
  Lit3formBlock,
  html,
  unsafeHTML,
  classMap,
} from '@Root/lib/lit-3form-block';
import { styles } from './css';
import './barrel';
import './customize-product-barrel';
import './customize-material-barrel';
import './order-sheet-barrel';
import './estimate-barrel';
import './cut-to-order-barrel';
import './customize-choices-barrel';
import './material-details-block';
import './product-details-block';

import {
  fetchCart,
  checkProductAvailability,
  addToCart,
} from '@Root/spree/api';
import {
  updateCartIndicator,
  changeProductNameInModal,
  changeProductSizeInfoInModal,
  changeProductImageInModal,
} from '@Root/spree/dom';
import {
  isFeatureDisabled,
  showOrderSheetBarrel,
  useSampleLimits,
  useAddToCartModal,
  useCartConfirmationModal,
  waitForElement,
  isDealerPage,
  logErrorToSentry,
  useMaterialDetailsBlock,
} from '../../../helpers/helpers';
import {
  handleFileDownloadEvent,
  analyticsCtaDataObject,
  analyticsLabelFromString,
  adobeCleanString,
  fireAdobeAnalyticsEvent,
  handleAdobeNavEvent,
} from '../../../helpers/adobe-analytics';
import '@Helpers/vimeo-video';

import find from 'lodash.find';

const imageSelectHashPrefix = '#w-image-';
const imageSelectHashRegExp = new RegExp(`${imageSelectHashPrefix}(\\d+)`);

export class Walter extends Lit3formBlock {
  static get characterName() {
    return 'walter';
  }

  static get characterStyles() {
    return [...styles];
  }

  static properties = {
    _selectedCarouselImageId: {
      type: Number,
      state: true,
      hasChanged: (newValue, oldValue) => {
        const hasChanged = newValue !== oldValue;

        if (hasChanged && window.location.hash.match(imageSelectHashRegExp))
          window.location.hash = `${imageSelectHashPrefix}${newValue + 1}`;

        return hasChanged;
      },
    },
    _carouselHeight: { type: Number },
    _barrels: { type: Array },
    _showMaterialDetailsBlockOnProductPage: { type: Boolean },
    _materialDetailsBlockData: { type: Array },
    _productDetailsBlockData: { type: Array },
    _variants: { type: Array },
    includedPropertiesById: { type: Object },
    primaryCtaHidden: { type: Boolean },
    primaryCtaDisabled: { type: Boolean },
    _hasCustomizeBarrel: { type: Boolean },
    _hasOrderSheetBarrel: { type: Boolean },
    _camera1ImageUrl: { type: String },
    _selectedCustomPattern: { type: Object },
    _selectedHardwareFinish: { type: Object },
    _barrelSelectedCarouselImages: { type: Array },
    _renderedProductImagesForMaterial: { type: Array },
    _carouselImagesArray: { type: Array },
    _loadedThreekitImages: { type: Object },
    _isEstimate: { type: Boolean },
    _addToCartQuantity: { type: Number },
    _showMobileStickyHeader: { type: Boolean },
    _carouselZoomEnabled: { type: Boolean },
    _fetchingNewCarouselImage: { type: Boolean },
  };

  constructor() {
    super();

    this._carouselImagesArray = this.content.images || [];

    const matches = window.location.hash.match(imageSelectHashRegExp),
      imageIndex = matches && matches[1] - 1;

    if (
      typeof imageIndex === 'number' &&
      imageIndex <= this._carouselImagesArray.length
    )
      this._selectedCarouselImageId = imageIndex;
    else this._selectedCarouselImageId = 0;

    this.formatBarrels(this.content.barrels);

    this._availableSampleTypes =
      typeof this.content.sample_types === 'object' &&
      Object.keys(this.content.sample_types).length > 0;

    if (!!this.content.sample_types)
      this.availableSampleTypes = Object.keys(this.content.sample_types);
    else this.availableSampleTypes = [];

    this.primaryCtaHidden = false;
    this.primaryCtaDisabled = true;
    this._hasCustomizeBarrel = false;
    this._hasOrderSheetBarrel = false;
    this._camera1ImageUrl = '';
    this._addToCartQuantity = 1;
    this._showMobileStickyHeader = false;
    this._carouselZoomEnabled = false;
    this._panelImageId = 'panel_image';
    this._fetchingNewCarouselImage = false;
    this._showMaterialDetailsBlockOnProductPage = false;

    this._isEstimate = this._barrels.some(
      barrel => barrel.hasOwnProperty('estimate') && !!barrel.estimate
    );

    this.addEventListener('customPatternSelected', e => {
      this._selectedCustomPattern = e.detail?.option || {};
      this.updateCarouselImages();
    });

    this.addEventListener('hardwareColorSelected', e => {
      this._selectedHardwareFinish = e.detail.option || {};
      this.updateCarouselImages();
    });

    this.addEventListener('newCarouselImagesFromBarrel', e => {
      this._barrelSelectedCarouselImages = e.detail.selectionImages || [];
      this.addThreekitImages(this._barrelSelectedCarouselImages);
    });

    this.addEventListener('productSelectedForMaterial', e => {
      this._renderedProductImagesForMaterial = e.detail.renderedImages || [];
      this.addThreekitImages(this._renderedProductImagesForMaterial, true);
    });

    this.addEventListener('requestMoreInfoFromBarrel', e => {
      this.requestMoreInfoClicked(e);
    });

    this.addEventListener('urlScrollToVisualize', e => {
      this.scrollToVisualize();
    });

    this.addEventListener('expandedOrderSheetBarrel', e => {
      this.selectCarouselImageFromDescription('panel');
      this.openOrderSheetDialog();
    });

    this.addEventListener('useWalterOrderSample', e => {
      this.openSampleOrderModal();
    });

    this.addEventListener('fetchingNewCarouselImage', e => {
      this._fetchingNewCarouselImage = true;
    });

    // Temporary workaround. This can be removed once the Bitreel software is
    // no longer pushing full sized images to this cookie.
    localStorage.removeItem('requestInfoData');

    this._loadedThreekitImages = {};
  }

  async connectedCallback() {
    super.connectedCallback();

    this.addVariants();

    if (!!this.content.vimeo_video_url) {
      this.addVideoToCarousel();
    }

    if (!!this.content.imajize_id) {
      this.addImajizeToCarousel();
    }

    if (this._isEstimate) {
      this.selectCarouselImageFromDescription('panel');
    }

    window.addEventListener('scroll', this._handleScroll.bind(this), false);
    document.addEventListener(
      'layout-shift-required',
      this._handleScroll.bind(this),
      false
    );
    // Initial trigger in case the page loads in a scrolled position
    this._handleScroll();

    try {
      await this.setupSpreeData();
    } catch (error) {
      this.primaryCtaHidden = true;
      logErrorToSentry(error);
    }

    this.setupCarouselHoverZoom();

    window.JSREPORT_READY_TO_START = true;
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._handleScroll.bind(this), false);
    document.addEventListener(
      'layout-shift-required',
      this._handleScroll.bind(this),
      false
    );
    super.disconnectedCallback();
  }

  async _handleScroll() {
    if (!window.useMobileLayout) {
      this._showMobileStickyHeader = false;
      return;
    }
    const walterBlockEl = await waitForElement('walter-block'),
      stickyWrapperEl = walterBlockEl.shadowRoot.getElementById(
        'sticky-hero-wrapper-mobile'
      ),
      stickyWrapperHeight = stickyWrapperEl
        ? stickyWrapperEl.getBoundingClientRect().height
        : 0,
      headerBottomPosition = walterBlockEl.shadowRoot
        .getElementById('hero-media')
        .getBoundingClientRect().bottom;

    const currentStatus = this._showMobileStickyHeader ? true : false;
    this._showMobileStickyHeader = headerBottomPosition <= stickyWrapperHeight;
    // apply logic only when _showMobileStickyHeader goes from false to true
    if (!currentStatus && this._showMobileStickyHeader) {
      if (
        this.selectedCarouselImage.is_video ||
        this.selectedCarouselImage.is_imajize
      ) {
        this.changeCarouselImage('next');
      }
    }

    const walterBox = walterBlockEl.getBoundingClientRect(),
      viewableWalter = walterBox?.height + walterBox?.y || 0;
    if (viewableWalter <= stickyWrapperHeight) {
      stickyWrapperEl.style['top'] = `${-Math.abs(
        stickyWrapperHeight - viewableWalter
      )}px`;
    } else {
      stickyWrapperEl.style['top'] = '0px';
    }
  }

  async setupCarouselHoverZoom() {
    const checkInterval = setInterval(() => {
      if (this.carouselImageContainer) {
        const container = this.carouselImageContainer;
        const containerRect = container.getBoundingClientRect();
        const image = this.heroImageElement;

        // Add click event listener to toggle mousemove event
        container.addEventListener('click', () => {
          if (window.innerWidth < 600) return;

          this._carouselZoomEnabled = !this._carouselZoomEnabled;

          if (this._carouselZoomEnabled) {
            container.addEventListener('mousemove', handleMouseMove);
            container.addEventListener('mouseleave', handleMouseLeave);
          } else {
            handleMouseLeave();
            container.removeEventListener('mousemove', handleMouseMove);
            container.removeEventListener('mouseleave', handleMouseLeave);
          }
        });

        const handleMouseMove = event => {
          if (!this._carouselZoomEnabled) return;

          // Image size in px
          const imageWidth = image.width;
          const imageHeight = image.height;

          // Mouse position relative to the container
          const mouseX = event.clientX - containerRect.left;
          const mouseY = event.clientY - containerRect.top;

          // Max translation values
          const maxTranslateX = imageWidth - container.offsetWidth;
          const maxTranslateY = imageHeight - container.offsetHeight;

          // Set the translation values based on mouse position
          const translateX = (mouseX / container.offsetWidth) * maxTranslateX;
          const translateY = (mouseY / container.offsetHeight) * maxTranslateY;

          if (translateX < 0) {
            image.style.transform = `translate(-50%, -${translateY}px)`;
            image.style.left = '50%';
          } else if (translateY < 0) {
            image.style.transform = `translate(-${translateX}px, -50%)`;
            image.style.top = '50%';
          } else {
            image.style.transform = `translate(-${translateX}px, -${translateY}px)`;
          }
        };

        const handleMouseLeave = () => {
          this._carouselZoomEnabled = false;
          image.style.removeProperty('transform');
          image.style.removeProperty('top');
          image.style.removeProperty('left');
        };

        clearInterval(checkInterval);
      }
    }, 10);
  }

  render() {
    return html`
      <div class="block-wrapper">
        <div class="block-grid">
          ${this.returnUrl
            ? html`
                <a class="all-patterns-btn print-none" href="${this.returnUrl}">
                  <img src="/static/icons/SVG/arrow-black-1px-4x8.svg" />
                  <p class="font-size-small">Back</p>
                </a>
              `
            : html``}
          <div
            class=${classMap({
              'carousel-images': true,
              'print-none': true,
              hidden: this.hideImageSelections,
            })}
          >
            <div
              class="carousel-images-sticky"
              style="height: ${this._carouselHeight}px"
            >
              ${this._carouselImagesArray.map((image, index) =>
                this.carouselImageHTML(image, index)
              )}
            </div>
          </div>
          <div class="carousel-hero">
            ${this.mobileStickyHeaderHTML}
            <div class="carousel-hero-content-wrapper" id="hero-wrapper">
              <div class="relative-position" id="carousel-hero-container">
                <div
                  class=${classMap({
                    'carousel-hero-image': true,
                    'zoom-enabled': this._carouselZoomEnabled,
                  })}
                  id="carousel-hero-image"
                >
                  ${this.selectedCarouselImage.is_video
                    ? html`
                        <vimeo-video
                          id="hero-media"
                          videoid="${this.videoId}"
                        ></vimeo-video>
                      `
                    : this.selectedCarouselImage.is_imajize
                    ? html`
                        <iframe
                          class="imajizeFrame"
                          id="hero-media"
                          src="${this.imajizeUrl}"
                          frameborder="0"
                          scrolling="no"
                          style="border: 0; position: absolute; top: 0; left: 0; width: 100%; height:100% ;"
                          allowfullscreen=""
                        ></iframe>
                      `
                    : html`
                        <img
                          id="hero-media"
                          class="carousel-img"
                          src=${this.heroImgUrl}
                          decoding="async"
                          @load=${this.carouselImageLoaded}
                        />
                      `}
                  ${this._fetchingNewCarouselImage
                    ? html`
                        <div class="loading-overlay">
                          <img
                            class="loading-spinner"
                            src="/static/images/3form-preloader.gif"
                            alt="Loading..."
                          />
                        </div>
                      `
                    : null}
                  <span
                    @click="${event => {
                      event.stopPropagation();
                      this.changeCarouselImage('previous', event);
                    }}"
                    class="${classMap({
                      'carousel-arrow': true,
                      'left-arrow': true,
                      hidden: this.hideImageSelections,
                    })}"
                  >
                    <img src="/static/icons/SVG/arrow-black-5x10.svg" />
                  </span>
                  <span
                    @click="${event => {
                      event.stopPropagation();
                      this.changeCarouselImage('next', event);
                    }}"
                    class="${classMap({
                      'carousel-arrow': true,
                      'right-arrow': true,
                      hidden: this.hideImageSelections,
                    })}"
                  >
                    <img src="/static/icons/SVG/arrow-black-5x10.svg" />
                  </span>
                </div>
              </div>
              <div class="carousel-bottom-row" id="hero-caption">
                <p class="font-size-small image-caption">
                  ${unsafeHTML(this.thumbnailDescription)}
                </p>
                ${this.selectedCarouselImage.is_video ||
                this.selectedCarouselImage.is_imajize
                  ? html``
                  : html`
                      <a
                        href=${this.selectedCarouselImage.full_resolution_src}
                        target="_blank"
                        class="print-none download-icon"
                        @click="${() =>
                          this.carouselDownloadIconClicked(
                            this.selectedCarouselImage
                          )}"
                      >
                        <span id="download-icon">
                          <p>Download Full Resolution Image</p>
                          <img src="/static/icons/SVG/download-12x16.svg" />
                        </span>
                      </a>
                    `}
              </div>
            </div>
          </div>
          <div class="title-header">
            ${this.blockLabel
              ? html`
                  <p class="block-label">${unsafeHTML(this.blockLabel)}</p>
                `
              : html``}
            <h1>${unsafeHTML(this.headerStr)}</h1>
          </div>
          <p class="description">${unsafeHTML(this.descriptionStr)}</p>
          ${this.ctaButtonsHTML}
          ${this._variants && this._variants.length > 1
            ? html`
                <div class="variants-wrapper print-none">
                  <span class="variants-header">
                    ${this.variantsHeaderText}
                  </span>
                  <div class="variants">
                    ${this._variants.map(
                      v => html`
                        <a
                          href="${v.link_path}"
                          class="variant"
                          @click="${event => {
                            const cleanName = adobeCleanString(v.name);
                            handleAdobeNavEvent({
                              navString: `NAV|Change Variant|${cleanName}`,
                              destinationUrl: v.link_path,
                              event,
                            });
                          }}"
                        >
                          <span>
                            <img
                              class=${this.isSelectedVariant(v)
                                ? 'selected-variant'
                                : 'not-selected-variant'}
                              src=${this.variantImgUrl(v)}
                            />
                            <p class="variant-name">${v.name}</p>
                          </span>
                        </a>
                      `
                    )}
                  </div>
                </div>
              `
            : html``}
          ${this._barrels.length > 0
            ? html`
                <div class="barrels">
                  ${this._barrels.map(this.renderBarrel.bind(this))}
                </div>
              `
            : html``}
          <div class="stupid-spacer"></div>
          ${this._productDetailsBlockData
            ? html`
                <product-details-block
                  content=${JSON.stringify(this._productDetailsBlockData)}
                ></product-details-block>
              `
            : html``}
          ${this._materialDetailsBlockData
            ? html`
                <material-details-block
                  class=${classMap({
                    'under-product-details': this
                      ._showMaterialDetailsBlockOnProductPage,
                  })}
                  content=${JSON.stringify(this._materialDetailsBlockData)}
                ></material-details-block>
              `
            : html``}
          ${this.horizontalRule}
          <img
            class="footer-logo print-only"
            src="/static/icons/SVG/3form-logo.svg"
          />
          <span class="footer-info print-only">
            <span>
              <p>800.726.0126</p>
              <p>info@3-form.com</p>
            </span>
            <span>
              <p>2300 South 2300 West</p>
              <p>Salt Lake City, UT 84119</p>
            </span>
            <span>
              <p>3-form.com</p>
            </span>
          </span>
        </div>
      </div>
    `;
  }

  get mobileStickyHeaderHTML() {
    return html`
      <div
        id="sticky-hero-wrapper-mobile"
        class=${classMap({
          'hide-sticky-wrapper': !this._showMobileStickyHeader,
        })}
      >
        <div
          class="carousel-hero-content-wrapper"
          id="hero-wrapper-mobile-walter"
        >
          <div class="carousel-hero-image">
            <img
              id="hero-img-mobile"
              class="carousel-img-sticky"
              src=${this.heroImgUrl}
              decoding="async"
              @load=${this.carouselImageLoaded}
            />
            ${this._fetchingNewCarouselImage
              ? html`
                  <div class="loading-overlay">
                    <img
                      class="loading-spinner"
                      src="/static/images/3form-preloader.gif"
                      alt="Loading..."
                    />
                  </div>
                `
              : null}
            <div
              class=${classMap({
                'carousel-arrow': true,
                'carousel-arrows-sticky': true,
                'print-none': true,
                hidden: this.hideImageSelections,
              })}
            >
              <span
                @click="${() => this.changeCarouselImage('previous', event)}"
                class="left-arrow"
              >
                <img src="/static/icons/SVG/arrow-black-5x10.svg" />
              </span>
              <span
                @click="${() => this.changeCarouselImage('next', event)}"
                class="right-arrow"
              >
                <img src="/static/icons/SVG/arrow-black-5x10.svg" />
              </span>
            </div>
          </div>
        </div>
        <div class="sticky-header-content">
          <h1>${unsafeHTML(this.headerStr)}</h1>
          <p class="image-caption">
            ${unsafeHTML(this.thumbnailDescription)}
          </p>
        </div>
      </div>
    `;
  }

  carouselImageLoaded() {
    this.setupCarouselHoverZoom();
    // Ensure that the image is rendered before setting the button height
    if (
      !this.heroImageElement ||
      this.heroImageElement.offsetHeight === 0 ||
      this.selectedCarouselImage.is_video ||
      this.selectedCarouselImage.is_imajize
    )
      return;

    this._fetchingNewCarouselImage = false;

    if (!!this.heroImageElementWrapper && !!this.heroImageElement) {
      this._carouselHeight = this.heroImageElementWrapper.offsetHeight;
    }
  }

  get primaryCtaClickHandler() {
    let clickHandler;
    if (JSON.parse(this.primaryCtaData).isSampleOrderCta && useSampleLimits()) {
      clickHandler = this.openSampleOrderModal;
    } else {
      clickHandler = this.addSelectedProductToCart;
    }
    return clickHandler;
  }

  get ctaButtonsHTML() {
    let renderSecondaryCta = false;
    const secondaryCta = {
      url: '',
      text: '',
      isSampleOrderCta: false,
    };
    let secondaryClickHandler;

    if (this._hasOrderSheetBarrel && this.renderOrderSheetsBarrel) {
      renderSecondaryCta = true;
      secondaryCta.text = 'Order 48"x 96" Panel';
      secondaryClickHandler = this.openOrderSheetDialog;
      secondaryCta.analyticsName = `INP|Open Order Panel|${adobeCleanString(
        this.headerStr
      )}`;
    } else if (this.secondaryCtaData) {
      renderSecondaryCta = true;
      secondaryCta.text = this.secondaryCtaData.text;
      secondaryCta.url = this.secondaryCtaData.url;
      if (this.isSampleOrderCta(this.secondaryCtaData.text)) {
        secondaryCta.isSampleOrderCta = true;
        secondaryClickHandler = this.openSampleOrderModal();
      }
      const cleanHeader = adobeCleanString(secondaryCta.text);
      const cleanCtaText = adobeCleanString(this.headerStr);
      secondaryCta.analyticsName = `INP|${cleanHeader}|${cleanCtaText}`;
    }

    return !this.content.direct_spree_product_id
      ? html`
          <div class="cta-btns">
            ${this.primaryCtaHidden
              ? html`
                  <cta-button
                    cta-data=${this.fallbackCtaData}
                    cta-type="primary"
                    @click="${this.requestMoreInfoClicked}"
                    .hidden="${this.fallbackCtaHidden ||
                      this._hasCustomizeBarrel}"
                    .disabled="${this.fallbackCtaDisabled}"
                    class="primary"
                  ></cta-button>
                `
              : html`
                  <cta-button
                    cta-data=${this.primaryCtaData}
                    cta-type="primary"
                    @click="${this.primaryCtaClickHandler}"
                    .hidden="${this.primaryCtaHidden ||
                      this._hasCustomizeBarrel}"
                    .disabled="${this.primaryCtaDisabled}"
                  ></cta-button>
                `}
            ${renderSecondaryCta
              ? html`
                  <cta-button
                    cta-data=${JSON.stringify(secondaryCta)}
                    cta-type="secondary"
                    @click="${secondaryClickHandler}"
                  ></cta-button>
                `
              : html``}
          </div>
        `
      : html`
          <div class="cta-btns">
            <div class="cta-btn-row" .hidden="${this.primaryCtaHidden}">
              <input
                type="number"
                name="quantity"
                @change=${this.directQtyChanged}
                .value="${this._addToCartQuantity}"
              />
              <cta-button
                cta-data=${this.directPrimaryCtaData}
                cta-type="primary"
                @click="${this.addSelectedProductToCart}"
                .disabled="${this.primaryCtaDisabled}"
              ></cta-button>
            </div>
          </div>
        `;
  }

  carouselImageHTML(image, index) {
    if (!!image[1].src || !!image[1].thumb_src) {
      return html`
        <div
          class="${classMap({
            image: true,
            'imajize-overlay': image[1].is_imajize,
          })}"
        >
          <img
            src=${this.thumbUrl(image[1])}
            data-index=${index}
            @click="${this._updateSelectedCarouselImage}"
            class="${this.isCurrentCarouselImage(image[1])
              ? 'selected-image'
              : 'not-selected-image'}"
          />
          ${this.imajizeOverlayHTML(image)}
        </div>
      `;
    } else {
      return html``;
    }
  }

  imajizeOverlayHTML(image) {
    if (!image[1].is_imajize) return html``;
    return html`
      <img
        src="/static/icons/SVG/360_icon_reversed.svg"
        class="image overlay"
      />
    `;
  }

  thumbUrl(image) {
    if (image.hasOwnProperty('thumb_src')) {
      return image.thumb_src;
    } else {
      return this.imagebossUrl(image.src, null, 100);
    }
  }

  updateCarouselImages() {
    if (!this.hasSelectedCustomPattern || !this.hasSelectedHardwareColor) {
      return;
    }
    this.fetchThreekitRenderedImages();
  }

  fetchThreekitRenderedImages() {
    const perPage = window.useMobileLayout ? '24' : '10';
    const searchParams = new URLSearchParams({
      threekit_hardware_finish_id: this._selectedHardwareFinish.id,
      threekit_stage_id: this._selectedCustomPattern.threekit_stage_id,
      render_interlayer_id: this._selectedCustomPattern.render_interlayer_id,
      sample_type_id: this._selectedCustomPattern.sample_type_id,
      perPage: perPage,
    });
    const layersUrl = `/threekit_layers?${searchParams}`;

    if (this._loadedThreekitImages[layersUrl]) {
      this.checkAddMaterialDetailsBlock(this._loadedThreekitImages[layersUrl]);
      return this.addThreekitImages(
        this._loadedThreekitImages[layersUrl].threekit_layers
      );
    }

    const init = {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    this._fetchingNewCarouselImage = true;

    fetch(layersUrl, init)
      .then(response => {
        if (response.ok) {
          return response.json();
        }
      })
      .then(json => {
        if (json && json?.threekit_layers) {
          this._loadedThreekitImages[layersUrl] = json;

          const { panel_image_src } = json;
          if (panel_image_src) {
            // add panel image to the carousel with the same object structure
            // so it receives the same treatment as the other rendered images
            this._loadedThreekitImages[layersUrl].threekit_layers.push({
              thumb_src: panel_image_src,
              carousel_src: panel_image_src,
              src: panel_image_src,
              full_resolution_src: panel_image_src,
              camera_id: this._panelImageId,
            });
          }

          this.addThreekitImages(
            this._loadedThreekitImages[layersUrl].threekit_layers
          );
          this.checkAddMaterialDetailsBlock(json);
        }
      })
      .catch(error => {
        logErrorToSentry(error);
      });

    this.sendAnalyticsVisualizationSelectedEvent();
  }

  sendAnalyticsImageDownloadEvent(image) {
    let filename = image.filename || '';
    const productName = this.headerStr || '';
    const patternName = this.configuredPatternName || '';
    const hardwareFinishName = this._selectedHardwareFinish?.display_name || '';
    const { image_category, adobeEventStr } = this.analyticsImageType();

    if (image_category === 'rendered' && typeof gtag === 'function') {
      gtag('event', `Visualization Download`, {
        event_category: 'Visualization',
        visualized_product: productName,
        visualized_pattern: patternName,
        visualized_hardware: hardwareFinishName,
      });
    }

    const downloadData = {
      file_format: 'JPG',
      file_name: analyticsLabelFromString(filename),
      link_url: image.full_resolution_src,
      link_type: 'IMAGE',
      placement: analyticsLabelFromString(adobeEventStr),
    };

    handleFileDownloadEvent(analyticsCtaDataObject(downloadData));

    if (typeof gtag === 'function') {
      gtag('event', `Carousel Image Download`, {
        event_category: 'Image Download',
        visualized_product: productName,
        visualized_pattern: patternName,
        visualized_hardware: hardwareFinishName,
        image_category,
      });
    }
  }

  analyticsImageType() {
    const description =
      this.selectedCarouselImage?.thumbnail_description?.toLowerCase() || '';
    const isRendered = this.selectedCarouselImage?.rendered;

    let image_category = '';
    let label = 'Default';

    if (isRendered) {
      image_category = 'rendered';
      label = 'Render';
    } else if (description.includes('drag to spin')) {
      image_category = 'imajize';
      label = 'Imajize';
    } else if (description.includes('panel')) {
      image_category = 'full-panel';
      label = 'Full Panel';
    } else if (description.includes('sample')) {
      image_category = 'sample';
      label = 'Sample';
    } else if (description.includes('detail')) {
      image_category = 'detail';
      label = 'Detail';
    }

    return {
      image_category,
      adobeEventStr: `Image ${label}`,
    };
  }

  sendAnalyticsCarouselImageSelect() {
    const formattedEventStr = analyticsLabelFromString(
      this.analyticsImageType().adobeEventStr
    );
    const eventData = {
      type: 'click',
      detail: {
        eVar8: `INP|Carousel Image Select|${formattedEventStr}`,
      },
    };
    fireAdobeAnalyticsEvent(eventData);
  }

  sendAnalyticsVisualizationSelectedEvent() {
    const productName = this.headerStr || '';
    const patternName = this.configuredPatternName || '';
    const hardwareFinishName = this._selectedHardwareFinish?.display_name || '';

    if (typeof gtag === 'function') {
      gtag('event', `Visualization View Product`, {
        event_category: 'Visualization',
        visualized_product: productName,
        visualized_pattern: patternName,
        visualized_hardware: hardwareFinishName,
      });
    }
  }

  sendAnalyticsVisualizationAvailable() {
    if (typeof gtag === 'function') {
      const productName = this.headerStr || '';
      gtag('event', `Visualization Barrel Available`, {
        event_category: 'Visualization',
        visualized_product: productName,
        visualization_barrel_available: true,
      });
    }
  }

  async addVideoToCarousel() {
    if (this.videoId) {
      await fetch(`https://vimeo.com/api/v2/video/${this.videoId}.json`)
        .then(response => {
          if (response.ok) {
            return response.json();
          }
        })
        .then(body => {
          if (body[0]) {
            const videoThumb =
              body[0].thumbnail_medium ||
              body[0].thumbnail_large ||
              body[0].thumbnail_small;
            const carouselObject = [
              'video',
              {
                visualization_swatch: '0',
                thumbnail_description: '',
                thumb_src: videoThumb,
                is_video: true,
                src: this.content.vimeo_video_url,
                full_resolution_src: '',
                display_order: this._carouselImagesArray.length,
              },
            ];
            this._carouselImagesArray.push(carouselObject);
          }
        });
    }
  }

  get videoId() {
    if (!!this.content.vimeo_video_url) {
      return this.content.vimeo_video_url.match(
        /^https?:\/\/(?:.*?)\.?(vimeo)\.com\/(\d+).*$/
      )[2];
    } else {
      return '';
    }
  }

  async addImajizeToCarousel() {
    if (this.imajizeId) {
      const carouselObject = [
        'imajize',
        {
          visualization_swatch: '0',
          thumbnail_description: '8" x 8" sample.\nClick & drag to spin.',
          thumb_src: this.imajizeThumb,
          is_video: false,
          is_imajize: true,
          src: this.imajizeUrl,
          full_resolution_src: '',
          display_order: 0,
        },
      ];
      this._carouselImagesArray.unshift(carouselObject);
    }
  }

  get imajizeId() {
    return this.content.imajize_id || '';
  }

  get imajizeUrl() {
    return `https://embed.imajize.com/${this.imajizeId}`;
  }

  get imajizeThumb() {
    return `https://fast.imajize.com/${this.imajizeId}/frames/1/w_150.jpg`;
  }

  addThreekitImages(threekitLayers = [], imagesToEndOfList = false) {
    let { _carouselImagesArray } = this;

    // remove any rendered images that are already in the carousel
    for (let i = 0; i < _carouselImagesArray.length; i++) {
      if (_carouselImagesArray[i][1].rendered) {
        _carouselImagesArray.splice(i, 1);
        i--;
      }
    }

    // Push the rendered images to the front of the _carouselImagesArray, but
    // in the order that they come from the server
    if (threekitLayers.length) {
      threekitLayers = imagesToEndOfList
        ? threekitLayers
        : threekitLayers.slice().reverse();
      threekitLayers.forEach((layer, index) => {
        if (layer.camera_id === 1) {
          this._camera1ImageUrl = layer.carousel_src;
          if (this.content.external_ng_component) {
            this.emitUpdatedProductImage();
          }
        }
        const formattedLayer = [];
        // display order will be sent by the server at some point
        layer.display_order = _carouselImagesArray.length + index;
        layer.rendered = true;
        layer.thumbnail_description = this.renderedImageThumbnailDescription(
          layer
        );
        formattedLayer.push(layer.display_order.toString());
        formattedLayer.push(layer);
        if (imagesToEndOfList) {
          this._carouselImagesArray.push(formattedLayer);
        } else {
          this._carouselImagesArray.unshift(formattedLayer);
        }
      });

      // set the selected image to the first rendered image in the list
      this._selectedCarouselImageId = this._carouselImagesArray.findIndex(
        img => img[1].rendered
      );
    }

    this._fetchingNewCarouselImage = false;
    this.requestUpdate();
  }

  renderedImageThumbnailDescription(layer) {
    let existingDescription = layer.thumbnail_description;
    const useElementsFormat =
        this.blockLabel.toLowerCase().includes('elements') ||
        isDealerPage() ||
        this.content.elements === '1',
      materialType = this._selectedCustomPattern
        ? this._selectedCustomPattern?.sample_type_display_name
        : this.materialType,
      material = `Material | ${materialType}`,
      pattern = this._selectedCustomPattern
        ? this._selectedCustomPattern.hex_code
          ? `Color | ${this.configuredPatternName}`
          : `Pattern | ${this.configuredPatternName}`
        : '',
      hardwarePrefix = useElementsFormat ? '' : 'Hardware | ',
      hardware =
        !!this.headerStr && !layer.productForMaterial
          ? `${hardwarePrefix}${this.blockLabel} ${this.headerStr}`
          : '',
      hardwareFinish =
        this._selectedHardwareFinish?.display_name &&
        this._selectedHardwareFinish?.display_name !== 'No Hardware Finish'
          ? `Hardware Finish | ${this._selectedHardwareFinish?.display_name}`
          : '';

    const defaultDisplayOrder = [
        existingDescription,
        hardware,
        hardwareFinish,
        pattern,
        material,
      ],
      elementsDisplayOrder = [
        hardware,
        existingDescription,
        hardwareFinish,
        pattern,
        material,
      ];

    let displayOrder = useElementsFormat
      ? elementsDisplayOrder
      : defaultDisplayOrder;

    if (layer.camera_id === this._panelImageId) {
      // description including 'panel' is needed for analytics
      existingDescription = `4' x 8' panel`;
      displayOrder = [existingDescription, pattern, material];
    }

    const existingDescriptionIndex = displayOrder.indexOf(existingDescription);

    // clean up empty data points and prevent duplicates
    displayOrder = displayOrder.map((dataPoint, index) => {
      if (!!existingDescription && index === existingDescriptionIndex) {
        return dataPoint;
      } else if (!dataPoint || existingDescription?.includes(dataPoint)) {
        return '';
      }
      return dataPoint;
    });

    return `${displayOrder.filter(Boolean).join('\n')}`;
  }

  get materialType() {
    let materialType = '';
    if (
      typeof this.content.sample_types === 'object' &&
      Object.keys(this.content.sample_types).length
    ) {
      materialType = Object.keys(this.content.sample_types)[0];
    } else if (this.content.sample_type_slug) {
      materialType = this.content.sample_type_slug;
    } else if (
      this.content.interlayer_variant_slug.includes('hush') ||
      this.content.interlayer_slug.includes('acoustic')
    ) {
      materialType = 'sola-felt';
    }
    materialType = materialType
      .replace(/-/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
    return materialType;
  }

  get configuredPatternName() {
    if (!this._selectedCustomPattern) return '';
    // sometimes only one or the other is available
    const { parentName, name } = this._selectedCustomPattern;
    return `${parentName || ''} ${name || ''}`.trim();
  }

  get hasSelectedHardwareColor() {
    return Object.keys(this._selectedHardwareFinish).length > 0;
  }

  get hasSelectedCustomPattern() {
    return (
      !!this._selectedCustomPattern &&
      typeof this._selectedCustomPattern === 'object' &&
      Object.keys(this._selectedCustomPattern).length > 0
    );
  }

  get heroImageElement() {
    return this.shadowRoot.getElementById('hero-media');
  }

  get heroImageElementWrapper() {
    return this.shadowRoot.getElementById('hero-wrapper');
  }

  get carouselImageContainer() {
    return this.shadowRoot.getElementById('carousel-hero-container');
  }

  isCurrentCarouselImage(image) {
    return image.src === this.selectedCarouselImage.src;
  }

  barrelHeaderIcon(isExpanded) {
    return isExpanded
      ? '/static/icons/SVG/minimize-dash.svg'
      : '/static/icons/SVG/expand-plus.svg';
  }

  _updateSelectedCarouselImage(event) {
    const index = event.target.getAttribute('data-index');
    this._selectedCarouselImageId = parseInt(index);
    this.sendAnalyticsCarouselImageSelect();
  }

  changeCarouselImage(direction, event = null) {
    const imagesCount = this._carouselImagesArray.length;
    const currentId = this._selectedCarouselImageId;
    if (direction === 'previous') {
      currentId === 0
        ? (this._selectedCarouselImageId = imagesCount - 1)
        : this._selectedCarouselImageId--;
    } else if (direction === 'next') {
      currentId === imagesCount - 1
        ? (this._selectedCarouselImageId = 0)
        : this._selectedCarouselImageId++;
    }
    if (
      (this._showMobileStickyHeader && this.selectedCarouselImage.is_video) ||
      (this._showMobileStickyHeader && this.selectedCarouselImage.is_imajize)
    ) {
      // skip videos in the sticky mobile header
      this.changeCarouselImage('next');
    }
    if (!!event) {
      handleAdobeNavEvent({
        navString: `INP|Hero Carousel Arrow|${direction}`,
        event,
      });
    }
  }

  addVariants() {
    if (!this.variantsUrl) return;

    const init = { method: 'GET', headers: { Accept: 'text/html' } };
    fetch(this.variantsUrl, init)
      .then(response => {
        if (response.ok) {
          return response.json();
        }
      })
      .then(json => {
        this.handleVariantsResponse(json);
      })
      .catch(error => {
        logErrorToSentry(error);
      });
  }

  async setupSpreeData() {
    this.cart = await fetchCart();
    if (this.cart) updateCartIndicator(this.cart.data.attributes.item_count);

    if (!this.productId) {
      this.primaryCtaHidden = true;

      // Notify the material details block whether it's cta should be disabled
      const options = {
        detail: {
          orderSampleShouldBeDisabled: this.primaryCtaDisabled,
          fallbackCtaData: this.fallbackCtaData,
        },
        bubbles: true,
        composed: true,
      };

      document.dispatchEvent(
        new CustomEvent('orderSampleShouldBeDisabled', options)
      );
      return;
    }

    this.includedPropertiesById = {};
    const productAvailabilityResponse = await checkProductAvailability(
      this.productId
    );

    if (productAvailabilityResponse.isFail()) {
      // Should be hidden and disabled
      this.primaryCtaHidden = true;
    } else {
      // Should be shown and enabled
      this.primaryCtaDisabled = false;

      this.includedPropertiesById[
        this.productId
      ] = productAvailabilityResponse.success().included;
    }
  }

  handleVariantsResponse(json) {
    this._variants = json;
  }

  isSelectedVariant(variant) {
    return this.interlayerVariantSlug === variant.slug;
  }

  variantImgUrl(variant) {
    // Image ratio for other images is 25/32
    // this adjusts the color chip images to cover that ratio from ImageBoss
    const height = variant.slug === 'color-chip' ? (100 / 25) * 32 : null;
    return this.imagebossUrl(variant.thumbnail_src, 100, height);
  }

  openSampleOrderModal() {
    useAddToCartModal();
    const setupAddToCartDataEvent = new CustomEvent('setupAddToCartData', {
      detail: {
        productVariantId: this.productVariantId,
        headerStr: this.headerStr,
        imageUrl: this.selectedSampleTypeImage,
        productSize: this.productSize,
        sampleType: this.selectedSampleTypeKey,
        gaugeStr: this.selectedSampleTypeProductGauge,
      },
      bubbles: true,
      composed: true,
    });
    const el = document.getElementById('modal-request-sample');
    el.dispatchEvent(setupAddToCartDataEvent);
    setTimeout(() => {
      // Wrapping setTimeout to avoid race condition
      this.showMicroModal();
    }, 0);
    // TODO: ADD ADOBE TRACKING HERE ... something like:
    // INP|Open Sample Order|${this.headerStr}
  }

  showMicroModal() {
    MicroModal.show('modal-request-sample');
  }

  emitUpdatedProductImage() {
    const options = {
      detail: {
        camera1ImageUrl: this._camera1ImageUrl,
      },
      bubbles: true,
      composed: true,
    };

    this.shadowRoot
      .getElementById('visualize')
      ?.dispatchEvent(
        new CustomEvent('renderedProductImageDidChange', options)
      );
  }

  async addSelectedProductToCart() {
    if (this.primaryCtaDisabled) return;
    useCartConfirmationModal();

    this.primaryCtaDisabled = true;

    this.cart = await addToCart(
      this.productVariantId,
      this._addToCartQuantity,
      this.selectedSampleTypeKey
    );
    if (this.cart) {
      updateCartIndicator(this.cart.item_count);

      const productSizeValue = this.productSize?.attributes?.value || '';

      changeProductSizeInfoInModal(
        productSizeValue,
        this.selectedSampleTypeDisplayValue
      );

      changeProductNameInModal(
        this.content.header.replace(` ${this.content.name}`, ''),
        this.content.name
      );
      changeProductImageInModal(this.selectedSampleTypeImage);
    }

    this.primaryCtaDisabled = false;
  }

  get productSize() {
    return this.includedPropertiesById
      ? find(this.includedPropertiesById[this.productId], {
          attributes: { name: 'size' },
        })
      : {};
  }

  carouselDownloadIconClicked(file) {
    this.sendAnalyticsImageDownloadEvent(file);
  }

  requestMoreInfoClicked(event) {
    const imageUrl = this._camera1ImageUrl
      ? this._camera1ImageUrl
      : this.imagebossUrl(this._carouselImagesArray[0][1].src, null, 700);

    const textDescription =
      this.hasSelectedCustomPattern || this._camera1ImageUrl
        ? this.thumbnailDescription
        : `${this.blockLabel} - ${this.headerStr}`;

    const sessionCookieData = {
      description: textDescription,
      image: {
        url: imageUrl,
        description: this.thumbnailDescription,
      },
    };

    window.sessionStorage.setItem(
      'requestInfoData',
      JSON.stringify(sessionCookieData)
    );

    const cleanName = adobeCleanString(event.detail?.cta);
    const label = adobeCleanString(`${this.blockLabel} - ${this.headerStr}`);
    handleAdobeNavEvent({
      navString: `NAV|${cleanName}|${label}`,
      destinationUrl: event.detail?.url,
      event,
    });
  }

  scrollToVisualize() {
    const scrollToVisualize = new CustomEvent('scrollToVisualize', {
      bubbles: true,
      composed: true,
    });

    const el = this.shadowRoot.getElementById('visualize');
    let yOffset = -30;
    if (window.useMobileLayout) {
      const stickyHeader = this.shadowRoot.getElementById(
        'sticky-hero-wrapper-mobile'
      );
      if (!!stickyHeader) {
        const offsetAdjustment =
          stickyHeader?.getBoundingClientRect().height || 208;
        yOffset = yOffset - offsetAdjustment;
      }
    }
    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;

    el.dispatchEvent(scrollToVisualize);
    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  openOrderSheetDialog() {
    this.selectCarouselImageFromDescription('panel');

    const openOrderSheetDialog = new CustomEvent('openOrderSheetDialog', {
      bubbles: true,
      composed: true,
    });

    document.dispatchEvent(openOrderSheetDialog);
  }

  selectCarouselImageFromDescription(term) {
    term = term.toLowerCase();
    const imageIndex = this._carouselImagesArray.findIndex(image => {
      const description = image[1]?.thumbnail_description?.toLowerCase() || '';
      return description.includes(term);
    });

    this._selectedCarouselImageId =
      typeof imageIndex === 'number' && imageIndex >= 0
        ? imageIndex
        : this._carouselImagesArray.length - 1;

    if (term === 'panel' && !this._isEstimate) {
      const options = {
        detail: {
          imageUrl: this._carouselImagesArray[this._selectedCarouselImageId],
        },
        bubbles: true,
        composed: true,
      };

      document.dispatchEvent(new CustomEvent('panel-image-selected', options));
    }
  }

  renderBarrel(barrel) {
    // Do not render barrels that are told to be hidden elsewhere
    if (barrel.hide) return;

    // We don't want to show the fifth download if this feature is disabled
    // since it will be a link to a PDF we don't want to be available yet.
    // This check needs to be here since user-based enabling will happen.
    const isVisualizationBarrel = !!barrel.page_variants_path;
    const isOrderSheetsBarrel =
      barrel.panel_ordering === '1' && !barrel.estimate;
    const isEstimateBarrel = barrel.estimate;
    const isCutToOrderBarrel = barrel.direct_product_choices;
    const isCustomizeChoicesBarrel = barrel.choices && barrel.choices.length;

    // The Material Details barrel and all barrels for direct pages
    // should default to open
    barrel.expanded =
      barrel.title === 'Material Details' ||
      !!this.content.direct_spree_product_id;

    if (
      isFeatureDisabled('materialDetailsPdfLink') &&
      barrel.downloads &&
      barrel.downloads.length > 4
    )
      barrel.downloads.pop();

    if (isVisualizationBarrel && !isCustomizeChoicesBarrel) {
      this._hasCustomizeBarrel = true;
      barrel.headerStr = this.headerStr;

      // add data for external NG component only when necessary to avoid
      // unnecessary loading of the external NG script. for now only
      // 'varia-stand-off-wall-feature' uses it so this will do
      if (
        this.content.external_ng_component === 'external-online-simple-spec'
      ) {
        barrel.externalNGData = {
          component: this.content.external_ng_component,
          technicalDocumentIds: this.content.technical_document_ids,
          renderedImageUrl: this._camera1ImageUrl,
          system: this.externalSystemValue,
        };
      }
      this.sendAnalyticsVisualizationAvailable();
      return html`
        <customize-product-barrel
          content=${JSON.stringify(barrel)}
          ?expanded=${barrel.expanded}
          id="visualize"
          class="print-none"
        ></customize-product-barrel>
      `;
    } else if (isCustomizeChoicesBarrel) {
      this._hasCustomizeBarrel = true;
      barrel.headerStr = this.headerStr;
      barrel.parentBlockId = this.content.id;
      return html`
        <customize-choices-barrel
          content=${JSON.stringify(barrel)}
          ?expanded=${barrel.expanded}
          id="visualize"
        ></customize-choices-barrel>
      `;
    } else if (isEstimateBarrel) {
      barrel.headerStr = this.headerStr;
      return html`
        <estimate-barrel
          content=${JSON.stringify(barrel)}
          ?expanded=${barrel.expanded}
        ></estimate-barrel>
      `;
    } else if (barrel.customizeProductBarrel) {
      return html`
        <customize-material-barrel
          content=${JSON.stringify(barrel)}
          class="carousel-height"
        ></customize-material-barrel>
      `;
    } else if (isOrderSheetsBarrel && this.renderOrderSheetsBarrel) {
      this._hasOrderSheetBarrel = true;
      barrel.headerStr = `${this.headerStr} 48" x 96" Panel Order`;
      barrel.productHeader = this.content.header || '';
      barrel.productSubheader = this.content.subheader || '';
      barrel.dialogId = 'order-sheet';
      return html`
        <dialog-modal id="dialog-modal" content=${JSON.stringify(barrel)}>
        </dialog-modal>
      `;
    } else if (isCutToOrderBarrel) {
      barrel.headerStr = this.headerStr;
      barrel.imgUrl = this.heroImgUrl;
      return html`
        <cut-to-order-barrel
          content=${JSON.stringify(barrel)}
          ?expanded=${barrel.expanded}
          id="cut-to-order"
        ></cut-to-order-barrel>
      `;
    } else if (
      !isVisualizationBarrel &&
      !isOrderSheetsBarrel &&
      !isEstimateBarrel &&
      !isCutToOrderBarrel
    ) {
      return html`
        <walter-barrel
          content=${JSON.stringify(barrel)}
          ?expanded=${barrel.expanded}
        ></walter-barrel>
      `;
    }
  }

  get renderOrderSheetsBarrel() {
    if (!showOrderSheetBarrel()) return false;
    const orderSheetsBarrel = this._barrels.find(
      barrel => barrel.panel_ordering === '1'
    );
    if (orderSheetsBarrel) {
      // Check that the barrel has the required information before rendering
      return (
        orderSheetsBarrel.pdma_gauges?.length > 0 &&
        orderSheetsBarrel.my3form_laminates?.length > 0 &&
        !orderSheetsBarrel.estimate
      );
    } else {
      return false;
    }
  }

  get blockLabel() {
    return this.content.label || '';
  }

  get productId() {
    if (this.content.direct_spree_product_id)
      return this.content.direct_spree_product_id;
    if (!this._availableSampleTypes) return false;

    return this.content.sample_types[this.availableSampleTypes[0]].product_id;
  }

  get productVariantId() {
    if (this.content.direct_spree_variant_id)
      return this.content.direct_spree_variant_id;
    if (!this._availableSampleTypes) return false;

    return this.content.sample_types[this.availableSampleTypes[0]].variant_id;
  }

  get selectedSampleTypeKey() {
    return this._availableSampleTypes && this.availableSampleTypes[0];
  }

  get selectedSampleTypeProductGauge() {
    return this.selectedSampleType && this.selectedSampleType.product_gauge;
  }

  get selectedSampleType() {
    return (
      this.selectedSampleTypeKey &&
      this.content.sample_types[this.selectedSampleTypeKey]
    );
  }

  get selectedSampleTypeDisplayValue() {
    return this.selectedSampleType && this.selectedSampleType.sample_type_text;
  }

  get selectedSampleTypeImage() {
    return (
      !!this.selectedSampleType &&
      !!this.selectedSampleType.image_src &&
      this.selectedSampleType.image_src.replace(':dimensions', 'width/258')
    );
  }

  get selectedCarouselImage() {
    return this.carouselImageIsSet
      ? this._carouselImagesArray[this._selectedCarouselImageId][1]
      : '';
  }

  get carouselImageIsSet() {
    return (
      this._carouselImagesArray[this._selectedCarouselImageId] &&
      this._carouselImagesArray[this._selectedCarouselImageId][1]
    );
  }

  get headerStr() {
    let header = this.content.header || '';
    this.content.subheader
      ? (header = header + ' ' + this.content.subheader)
      : '';
    return this.withLineBreaks(header);
  }

  get descriptionStr() {
    return this.withLineBreaks(this.content.description) || '';
  }

  get variantsHeaderText() {
    if (
      this.content.page_type === 'ProductCollection' ||
      this.content.page_type === 'Product'
    ) {
      return `${this.content.interlayer_name} Collection`;
    } else {
      return `${this.content.interlayer_name} Colors & Variants`;
    }
  }

  get returnUrl() {
    if (!this.content.return_url) return false;
    let returnUrl = this.content.return_url;

    const filterOptions = this.getCookie('filterOptions');
    const searchTerm = this.getCookie('searchTerm');

    const url = new URL(returnUrl, window.location.origin);

    // Remove any preexisting parameters
    url.searchParams.delete('filterOptions');
    url.searchParams.delete('searchTerm');

    if (filterOptions) {
      const decodedFilterOptions = decodeURIComponent(filterOptions);
      url.searchParams.set('filterOptions', decodedFilterOptions);
    }

    if (searchTerm) {
      const decodedSearchTerm = decodeURIComponent(searchTerm);
      url.searchParams.set('searchTerm', decodedSearchTerm);
    }

    const sampleType =
      new URLSearchParams(window.location.search).get('sample_type') || '';

    if (sampleType) {
      url.pathname = `samples/${sampleType}`;
    }

    // Manually reconstruct href to prevent auto-encoding issues
    returnUrl = !!url.searchParams?.size
      ? `${url.pathname}?${url.searchParams.toString()}`
      : `${url.pathname}`;

    return returnUrl;
  }

  getCookie(name) {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? decodeURIComponent(match[2]) : null;
  }

  get thumbnailDescription() {
    return (
      this.withLineBreaks(this.selectedCarouselImage.thumbnail_description) ||
      ''
    );
  }

  get primaryCtaData() {
    if (this.content.cta) {
      this.content.cta.fullWidth = true;
      this.content.cta.isSampleOrderCta = this.isSampleOrderCta(
        this.content.cta?.text
      );
    }
    return JSON.stringify(this.content.cta);
  }

  get secondaryCtaData() {
    if (!this.content.secondary_cta_text) return null;
    return {
      text: this.content.secondary_cta_text,
      url: this.content.secondary_cta_url,
      isSampleOrderCta: this.isSampleOrderCta(this.content.secondary_cta_text),
      fullWidth: true,
    };
  }

  isSampleOrderCta(ctaData) {
    const text = typeof ctaData === 'object' ? ctaData.text : ctaData;
    return !!text?.toLowerCase()?.includes('sample');
  }

  get jesseDriverCtaData() {
    return JSON.stringify({
      text: 'Visualize Options',
      url: '#hardware-systems',
      fullWidth: true,
      isSampleOrderCta: false,
    });
  }

  get directPrimaryCtaData() {
    return JSON.stringify({
      text: 'Add to Cart',
      url: '',
      fullWidth: true,
    });
  }

  directQtyChanged(e) {
    if (e?.target?.value && Number(e?.target?.value) > 0) {
      this._addToCartQuantity = Number(e.target.value);
    }
  }

  get fallbackCtaData() {
    const fallbackCta = this.content.fallback_cta;
    if (typeof fallbackCta === 'object') {
      fallbackCta.fullWidth = true;
      fallbackCta.isSampleOrderCta = this.isSampleOrderCta(fallbackCta?.text);
      const cleanHeader = adobeCleanString(fallbackCta.text);
      const cleanCtaText = adobeCleanString(this.headerStr);
      // fallbackCta.analyticsName = `INP|Fallback CTA ${cleanHeader}|${cleanCtaText}`;
    }
    return JSON.stringify(fallbackCta);
  }

  get fallbackCtaHidden() {
    return !this.primaryCtaHidden;
  }

  get fallbackCtaDisabled() {
    return this.fallbackCtaHidden;
  }

  get interlayerVariantSlug() {
    return this.content.interlayer_variant_slug;
  }

  formatBarrels(barrels) {
    let formattedBarrels = barrels || [];

    formattedBarrels.forEach((b, index) => {
      b.expanded = true;
      b.order = index;

      // the customize-product-barrel is defaulted to open so it needs to be last
      if (!!b.page_variants_path) {
        b.order = formattedBarrels.length;
      }
    });

    formattedBarrels.sort((a, b) => a.order - b.order);

    if (this.useMaterialDetailsBlock) {
      this.setupMaterialDetailsBlock(formattedBarrels, null);
    }

    ///////////////////////////
    // Product Details Block //
    ///////////////////////////
    if (this.useProductDetailsBlock) {
      let productDetailsBlockData = [];
      // For the material details block we extract the following barrels from
      // the Walter and use them in the details block.
      const barrelsToExtract = [
        'Material Details',
        'Product Details',
        'Sustainability',
        'Downloads',
      ];
      barrelsToExtract.forEach(title => {
        const formattedBarrelsIndex = formattedBarrels.findIndex(
          obj => obj.title === title
        );
        if (formattedBarrelsIndex !== -1) {
          productDetailsBlockData.push(formattedBarrels[formattedBarrelsIndex]);
          formattedBarrels.splice(formattedBarrelsIndex, 1);
        }
      });

      // Format the exracted barrel data and make it available to the block
      if (productDetailsBlockData.length) {
        const sampleObj = Object.values(this.content.sample_types)[0];
        let sampleImgUrl = '';
        if (sampleObj) {
          sampleImgUrl = sampleObj?.image_src;
        } else {
          sampleImgUrl =
            this.sampleImageFromCarousel[1]?.src ||
            this._carouselImagesArray[0][1]?.src ||
            '';
        }
        sampleImgUrl = sampleImgUrl.replace(':dimensions', 'width/258');
        const fullPanelOrdering =
          this._hasOrderSheetBarrel && this.renderOrderSheetsBarrel;

        this._productDetailsBlockData = {
          productData: {
            product: this.productType,
            headerString: this.headerStr,
            imageUrl: sampleImgUrl,
            fullPanelOrdering,
          },
          barrelsData: productDetailsBlockData,
        };
      }

      if (this.content.render_interlayer_id) {
        const {
          threekit_stages_path,
          threekit_hardware_finishes_path,
          render_interlayer_id,
        } = this.content;
        const sample_type_id =
          typeof this.content.sample_types === 'object'
            ? Object.values(this.content.sample_types)[0]?.id
            : null;
        const customizeProductBarrelData = {
          customizeProductBarrel: true,
          threekit_stages_path,
          threekit_hardware_finishes_path,
          render_interlayer_id,
          sample_type_id,
          productType: this.productType,
        };
        formattedBarrels.push(customizeProductBarrelData);
      }
    }

    this._barrels = formattedBarrels;
  }

  checkAddMaterialDetailsBlock(json) {
    const pageVariantContent = json.page_variant_content;
    if (pageVariantContent && Object.keys(pageVariantContent).length > 0) {
      this._showMaterialDetailsBlockOnProductPage = true;
      this.setupMaterialDetailsBlock(null, json);
    }
  }

  setupMaterialDetailsBlock(formattedBarrels, dataFromEvent = null) {
    this._materialDetailsBlockData = null;
    let isMaterialOnProductPage = false;
    if (!formattedBarrels) {
      formattedBarrels = dataFromEvent.page_variant_content?.barrels || [];
      formattedBarrels.sort((a, b) => a.order - b.order);
      this._showMaterialDetailsBlockOnProductPage = true;
      isMaterialOnProductPage = true;
    }
    let materialDetailsBlockData = [];
    // For the material details block we extract the following barrels from
    // the Walter and use them in the details block.
    const barrelsToExtract = [
      'Material Details',
      'Sustainability',
      'Downloads',
    ];
    barrelsToExtract.forEach(title => {
      const formattedBarrelsIndex = formattedBarrels.findIndex(
        obj => obj.title === title
      );
      if (formattedBarrelsIndex !== -1) {
        materialDetailsBlockData.push(formattedBarrels[formattedBarrelsIndex]);
        formattedBarrels[formattedBarrelsIndex].hide = true;
      }
    });

    const sampleDataFromEvent = dataFromEvent
      ? Object.values(dataFromEvent.page_variant_content?.sample_types)[0]
      : null;
    // Format the exracted barrel data and make it available to the block
    if (materialDetailsBlockData.length) {
      const sampleObj = Object.values(this.content.sample_types)[0];
      let sampleImgUrl = '';
      if (sampleObj) {
        sampleImgUrl = sampleObj?.image_src;
      } else if (isMaterialOnProductPage) {
        sampleImgUrl = sampleDataFromEvent?.image_src;
      } else {
        sampleImgUrl =
          this.sampleImageFromCarousel[1]?.src ||
          this._carouselImagesArray[0][1]?.src ||
          '';
      }
      sampleImgUrl = sampleImgUrl?.replace(':dimensions', 'width/258') || '';
      const fullPanelOrdering =
        this._hasOrderSheetBarrel && this.renderOrderSheetsBarrel;
      const sampleMaterialTypeText = isMaterialOnProductPage
        ? Object.keys(dataFromEvent.page_variant_content?.sample_types)[0]
        : '';
      const capitalizedSampleMaterialTypeText =
        sampleMaterialTypeText.charAt(0).toUpperCase() +
        sampleMaterialTypeText.slice(1);

      const material = isMaterialOnProductPage
        ? capitalizedSampleMaterialTypeText
        : this.materialType;
      const headerString = isMaterialOnProductPage
        ? dataFromEvent.page_variant_content?.header
        : this.headerStr;
      const sampleData = sampleDataFromEvent
        ? {
            productVariantId: sampleDataFromEvent.variant_id,
            headerStr: headerString,
            imageUrl: sampleImgUrl,
            sampleType: capitalizedSampleMaterialTypeText,
            gaugeStr: sampleDataFromEvent.product_gauge,
          }
        : null;

      this._materialDetailsBlockData = {
        materialData: {
          material,
          headerString,
          imageUrl: sampleImgUrl,
          fullPanelOrdering,
        },
        barrelsData: materialDetailsBlockData,
        ...(!!sampleData && { sampleData }),
      };
    }
    this.requestUpdate();
  }

  get sampleImageFromCarousel() {
    return (
      this._carouselImagesArray.find(image =>
        image[1].thumbnail_description.toLowerCase().includes('sample')
      ) || []
    );
  }

  get useMaterialDetailsBlock() {
    return (
      this.content.page_type === 'Interlayer' ||
      this.content.page_type === 'Color' ||
      this._showMaterialDetailsBlockOnProductPage
    );
  }

  get useProductDetailsBlock() {
    return (
      this.content.page_type === 'Product' ||
      this.content.page_type === 'ProductCollection'
    );
  }

  get externalSystemValue() {
    switch (this.content.interlayer_slug) {
      case 'frameless-suspended-partition':
        return 'Suspend';
      case 'varia-stand-off-wall-feature':
        return 'Point Support';
      default:
        return null;
    }
  }

  get variantsUrl() {
    return this.content.variants_url;
  }

  get hideImageSelections() {
    return this._carouselImagesArray.length <= 1;
  }

  get heroImgUrl() {
    if (this.selectedCarouselImage.hasOwnProperty('carousel_src')) {
      return this.selectedCarouselImage.carousel_src;
    } else {
      // Image ratio for other images is 137/175
      // this adjusts the color chip images to cover that ratio from ImageBoss
      const width =
        this.content.interlayer_variant_slug === 'color-chip'
          ? (700 / 175) * 137
          : null;
      return this.imagebossUrl(this.selectedCarouselImage.src, width, 1000);
    }
  }
}
Walter.defineElement();
